/// <amd-module name="Tfs/Platform/Search/GlobalSearchCoordinator" />
import { ObservableValue } from "VSS/Core/Observable";
import { Services, VssService } from "VSS/Platform/Context";
import { isFeatureEnabled, isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { FastPageSwitch } from "VSS/Platform/FPS";
import { routeUrl } from "VSS/Platform/Location";
import { IVssNavigationService } from "VSS/Platform/Navigation";
import { ISearchCoordinator, ISearchResult, ISearchStatus } from "VSS/Platform/Search";
import { ITfsPageService } from "../Page";
import { ITfsSearchQuery } from "../Search";

/**
 * WorkItemSearchCoordinator is used to route all searches to the work item search (queries page).
 * This is the default for TFS Search when the Search Extension is not availble.
 */
class WorkItemSearchCoordinator extends VssService implements ISearchCoordinator {
    public execute(searchQuery: ITfsSearchQuery): Promise<ISearchStatus> {
        const pageService = this.pageContext.getService<ITfsPageService>("ITfsPageService");
        const pageData = pageService.getData();

        // Setup the basic routing information used to this search query.
        const routeValues: { [key: string]: string } = { searchText: searchQuery.queryText };

        // If this is a project page we will generate a project targetted page, otherwise a collection page.
        if (pageData && pageData.project) {
            routeValues.project = pageData.project.name;

            const teamData = pageService.getHorizontalNavigationTeamData();
            if (teamData) {
                routeValues.team = teamData.name;
            }
        }

        const searchUrl = routeUrl(this.pageContext, "ms.vss-work-web.query-search:route", routeValues);
        if (searchUrl) {
            if (searchQuery.openInNewWindow) {
                window.open(searchUrl);
            } else {
                FastPageSwitch(this.pageContext, searchUrl, true);
            }
        }

        return Promise.resolve({ searchQuery: searchQuery, searchResult: new ObservableValue<ISearchResult | undefined>(undefined) });
    }
}

Services.add("witsearch-coordinator", { serviceFactory: WorkItemSearchCoordinator });

/**
 * TfsSearchCoordinator is used by the Search Extension to form and route requests to the search pages.
 * This is available when the Search extension is installed.
 */
class TfsSearchCoordinator extends VssService implements ISearchCoordinator {
    public execute(searchQuery: ITfsSearchQuery): Promise<ISearchStatus> {
        const pageService = this.pageContext.getService<ITfsPageService>("ITfsPageService");
        const pageData = pageService.getData();
        const teamData = pageService.getHorizontalNavigationTeamData();
        const wikiSearch =
            searchQuery.providerId === "ms.vss-wiki-web.wiki-project-search-provider" ||
            searchQuery.providerId === "ms.vss-wiki-web.wiki-collection-search-provider";

        // Setup the basic routing information used to this search query.
        const routeValues: { [key: string]: string } = { text: searchQuery.queryText, type: searchQuery.type };
        let routeId = "ms.vss-search-web.collection-route";
        const hashParams = "";

        // If this is a project page we will generate a project targetted page, otherwise a collection page.
        if (pageData && pageData.project) {
            routeId = "ms.vss-search-web.project-route";
            routeValues.project = pageData.project.name;

            if (teamData) {
                routeValues.team = teamData.name;
            }
        }

        let launchPoint = "";

        // Mapping of selected Hub group to search launch point prefix.
        const hubNames: { [contributionId: string]: string } = {
            "ms.vss-wiki-web.wiki-hub-group": "wiki",
            "ms.vss-code-web.code-hub-group": "code",
            "ms.vss-work-web.work-hub-group": "workitems",
            "ms.vss-build-web.build-release-hub-group": "buildrelease",
            "ms.vss-test-web.test-hub-group": "test",
            "ms.vss-tfs-web.project-team-hidden-hub-group": "projects-team",
            "ms.vss-web.home-hub-group": "dashboard",
            "ms.vss-web.project-admin-hub-group": "projects-admin",
            "ms.vss-tfs-web.collection-project-hub-group": "apps-projects",
            "ms.vss-tfs-web.collection-favorites-hub-group": "apps-favorites",
            "ms.vss-tfs-web.collection-work-hub-group": "apps-workitems",
            "ms.vss-tfs-web.collection-pullrequests-hub-group": "apps-pullrequests",
            "ms.vss-tfs-web.collection-rooms-new-account-nav-hub-group": "apps-rooms",
            "ms.vss-tfs-web.collection-users-new-account-nav-hub-group": "apps-users",
            "ms.vss-web.collection-admin-hub-group": "apps-admin"
        };

        // Compute the launchPoint parameter for this search request.
        const navigationService = this.pageContext.getService<IVssNavigationService>("IVssNavigationService");
        const displayedNavigation = navigationService.getDisplayedNavigation();
        const selectedHubGroupId = displayedNavigation.length > 1 && displayedNavigation[2];

        if (!selectedHubGroupId) {
            launchPoint = "search";
        } else if (hubNames[selectedHubGroupId]) {
            launchPoint = hubNames[selectedHubGroupId];
        } else {
            launchPoint = "custom";
        }

        if (pageData) {
            if (teamData) {
                launchPoint += "-Team";
            } else if (pageData.project) {
                launchPoint += "-Project";
            } else {
                launchPoint += "-Collection";
            }
        }

        routeValues.lp = encodeURIComponent(launchPoint);

        // No need to add any filter in case of wikisearch(project scoped API takes care of applying project filter).
        if (!wikiSearch) {
            let filters = "";

            // Build the explicit filter parameter list.
            if (searchQuery.filters) {
                for (const filter in searchQuery.filters) {
                    const filterValue: string[] | undefined = searchQuery.filters[filter];
                    if (filterValue) {
                        filters += filter + "{" + filterValue[0] + "}";
                    }
                }
            }

            routeValues.filters = filters;
        }

        if (searchQuery.pageSize) {
            routeValues.pageSize = searchQuery.pageSize.toString();
        }

        if (searchQuery.includeFacets !== undefined) {
            routeValues.includeFacets = String(searchQuery.includeFacets);
        }

        // Generate the target URL with the optional hash parameters.
        const searchUrl = routeUrl(this.pageContext, routeId, routeValues) + hashParams;
        if (searchUrl) {
            if (searchQuery.openInNewWindow) {
                window.open(searchUrl);
            } else {
                FastPageSwitch(this.pageContext, searchUrl, true);
            }
        }

        return Promise.resolve({ searchQuery: searchQuery, searchResult: new ObservableValue<ISearchResult | undefined>(undefined) });
    }
}

Services.add("tfssearch-coordinator", { serviceFactory: TfsSearchCoordinator });
